import {
  IRequirementResponse,
  RequirementStatus
} from "@wingspanhq/payments/dist/interfaces";
import { RequirementContextType } from "../types";
import { selectorIsRequirementPostApproval } from "../selectors/selectorIsRequirementPostApproval";
import { selectorIsRequirementExpiringSoon } from "../selectors/selectorIsRequirementExpiringSoon";
import { selectorIsRequirementExpiringInDaysFormatted } from "../selectors/selectorIsRequirementExpiringInDaysFormatted";
import { selectorIsRequirementExpired } from "../selectors/selectorIsRequirementExpired";
import {
  toWSDateString,
  WSElementColorsType
} from "@wingspanhq/fe-component-library";
import { selectorIsRequirementBeforeExpiration } from "../selectors/selectorIsRequirementBeforeExpiration";

export const getStatusSecondaryTextPrefix = (
  requirement: IRequirementResponse,
  requirementContextType: RequirementContextType
): string => {
  switch (requirement.payerOverrideStatus) {
    case RequirementStatus.Complete:
      return requirementContextType === RequirementContextType.Payee
        ? "Marked complete by you"
        : "Marked complete by client";
    case RequirementStatus.Incomplete:
      return requirementContextType === RequirementContextType.Payee
        ? "Marked incomplete by you"
        : "Marked incomplete by client";
    default:
      return "";
  }
};

export const extendRequirementStatusSecondaryTextProps = (
  requirement: IRequirementResponse,
  requirementContextType: RequirementContextType,
  originalRequirementStatusText: string | undefined
): {
  secondaryText: string | undefined;
  secondaryTextColor: WSElementColorsType | undefined;
} => {
  const prefix = getStatusSecondaryTextPrefix(
    requirement,
    requirementContextType
  );

  const isReadyForApprove =
    requirement.status === RequirementStatus.PendingReview;

  const isPostApproval = selectorIsRequirementPostApproval(requirement);

  let defaultText = "";

  if (isReadyForApprove) {
    if (requirementContextType === RequirementContextType.Payee) {
      defaultText = "Awaiting your approval";
    } else {
      defaultText = "Awaiting client approval";
    }
  } else if (isPostApproval) {
    if (requirementContextType === RequirementContextType.Payee) {
      defaultText =
        requirement.status === RequirementStatus.Complete
          ? "Approved by you"
          : "Rejected by you";
    } else {
      defaultText =
        requirement.status === RequirementStatus.Complete
          ? "Approved by client"
          : "Rejected by client";
    }
  } else if (requirement.payerOverrideStatus) {
    defaultText = prefix;
  }

  const mainText = defaultText || originalRequirementStatusText || "";

  let expirationText =
    requirement.status === RequirementStatus.Complete &&
    selectorIsRequirementBeforeExpiration(requirement) &&
    requirement.expiresAt
      ? `Effective ${toWSDateString(requirement.expiresAt, "input")}`
      : selectorIsRequirementExpired(requirement)
      ? "Expired"
      : selectorIsRequirementExpiringSoon(requirement)
      ? `Expiring ${selectorIsRequirementExpiringInDaysFormatted(requirement)}`
      : null;

  const secondaryText = [expirationText, mainText].filter(Boolean).join(" • ");

  const secondaryTextColor =
    requirement.status === RequirementStatus.Complete
      ? undefined
      : selectorIsRequirementExpired(requirement)
      ? "red300"
      : selectorIsRequirementExpiringSoon(requirement) ||
        (isReadyForApprove &&
          requirementContextType === RequirementContextType.Payee)
      ? "amber400"
      : undefined;

  return { secondaryText, secondaryTextColor };
};
